import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./ImageSlider.css";
import axios from "axios";

// Helper function to process image URLs
const getImageUrl = (imageUrl) => {
  console.log("Processing image URL:", imageUrl); // Debug log

  // If no imageUrl, return placeholder
  if (!imageUrl) {
    return "/placeholder-image.jpg";
  }

  // Check if the URL is already a complete URL (Cloudinary URLs or other CDN URLs)
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // Handle relative paths
  if (imageUrl.startsWith('/')) {
    // If it starts with /, it's an absolute path from the server root
    const baseUrl = process.env.REACT_APP_BASE_URL?.split("/api")[0] || window.location.origin;
    return `${baseUrl}${imageUrl}`;
  }

  // Handle paths without leading slash
  const baseUrl = process.env.REACT_APP_BASE_URL?.split("/api")[0] || window.location.origin;
  return `${baseUrl}/${imageUrl}`;
};

const ImageSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [images, setImages] = useState([]);

  useEffect(() => {
    const fetchSliderImages = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/sliders/allsliders`);

        setImages(response.data.sliders.map(slider => {
          console.log("Original slider imageUrl:", slider.imageUrl); // Debug log

          return {
            src: getImageUrl(slider.imageUrl), // Use the helper function here
            alt: slider.title || `Slide ${slider.id}` // Add alt text if available
          };
        }));
      } catch (error) {
        console.error("Error fetching slider images:", error);
      }
    };
    fetchSliderImages();
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentIndex(prevIndex => (images.length > 0 ? (prevIndex + 1) % images.length : 0));
  }, [images]);

  const prevSlide = useCallback(() => {
    setCurrentIndex(prevIndex => (images.length > 0 ? (prevIndex - 1 + images.length) % images.length : 0));
  }, [images]);

  useEffect(() => {
    if (!isHovering && images.length > 0) {
      const interval = setInterval(nextSlide, 5000);
      return () => clearInterval(interval);
    }
  }, [nextSlide, isHovering, images]);

  return (
    <div className="advanced-slider-container"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}>
      <div className="advanced-slider-wrapper"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
        {images.length > 0 ? (
          images.map((image, index) => (
            <div className="advanced-slide" key={index}>
              <img
                src={image.src}
                alt={image.alt || `Slide ${index + 1}`}
                className="advanced-slide-image"
                onError={(e) => {
                  console.error("Image failed to load:", image.src);
                  e.target.src = getImageUrl("/placeholder-image.jpg"); // Use helper for fallback too
                }}
                onLoad={() => {
                  console.log("Image loaded successfully:", image.src); // Debug log
                }}
                style={{ backgroundImage: `url(${image.src})` }}
              />
            </div>
          ))
        ) : (
          <div className="loading-container">
            <p>Loading images...</p>
          </div>
        )}
      </div>

      {images.length > 1 && (
        <>
          <button className="slider-nav-button prev-button" onClick={prevSlide}>
            <ChevronLeft size={24} />
          </button>
          <button className="slider-nav-button next-button" onClick={nextSlide}>
            <ChevronRight size={24} />
          </button>
        </>
      )}

      {images.length > 1 && (
        <div className="advanced-slider-dots">
          {images.map((_, index) => (
            <span
              key={index}
              className={`dot ${index === currentIndex ? "active" : ""}`}
              onClick={() => setCurrentIndex(index)}
            ></span>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageSlider;